//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  AffiliateType,
  CreatorPortalProfile,
} from '@apis/creators-campaigns/CreatorsCampaignsApiModel';
import {
  CreatorCampaignPayoutBreakdown,
  MonetaryValue,
} from '@campaigns/components/campaign-requests/creator-campaigns/CreatorsCampaignsTypes';
import {
  CampaignCreatorInfo,
  SocialCreatorsPlatforms,
} from '@models/CreatorsTypes';
import { PaginatedResponse } from '@models/PaginationTypes';

export interface ExchangeRate {
  rate?: number;
  sourceCurrency?: string;
}

export interface BaseCreateDebitTransactionsRequest {
  // amount must be in a valid amount string format, i.e. 2 decimal places
  // eg. 100.00, 100.0 is not accepted by the api`
  amount: string;
  currency?: string;
  tags?: string[];
}

export interface CreateDebitTransactionsRequestObject
  extends BaseCreateDebitTransactionsRequest {
  consumerRecipientId: string;
}

export interface CreateOfflineDebitTransactionsRequestObject
  extends BaseCreateDebitTransactionsRequest {
  username: string;
  socialNetwork: SocialCreatorsPlatforms;
  creatorId: string;
}

export interface CreateDebitTransactionsRequest {
  debitTransactionRequest: CreateDebitTransactionsRequestObject[];
}

export interface CreateOfflineDebitTransactionsRequest {
  debitTransactionRequest: CreateOfflineDebitTransactionsRequestObject[];
  campaignSlugName: string;
}

export const PaymentsPayoutMethods = {
  BANK_TRANSFER: 'BANK_TRANSFER',
  OFFLINE: 'OFFLINE',
} as const;

export type PaymentsPayoutMethodsType = keyof typeof PaymentsPayoutMethods;

export interface CreateDebitTransactionsResponse {
  success?: PaymentsTransactionObject[];
  failed?: PaymentsTransactionObject[];
}

export const PaymentsTransactionStatus = {
  FAILED: 'FAILED',
  PAID: 'PAID',
  INITIATED: 'INITIATED',
  PROCESSING: 'PROCESSING',
} as const;

export type PaymentsTransactionStatusType =
  keyof typeof PaymentsTransactionStatus;

export const PaymentsTransactionTypeTitle = {
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT',
} as const;

export type PaymentsTransactionType = keyof typeof PaymentsTransactionTypeTitle;

export interface PaymentsTransactionObject {
  amount?: string;
  campaignSlugName?: string;
  createdAt?: Date;
  creatorId?: string;
  creatorNetwork?: SocialCreatorsPlatforms;
  currency?: string;
  failureMessage?: string;
  payoutMethod?: PaymentsPayoutMethodsType;
  status: PaymentsTransactionStatusType;
  transactionId?: string;
  transactionType: PaymentsTransactionType;
  updatedAt?: Date;
  userId?: string;
}

export interface CreatorsPaymentLinkRequest {
  creators: PaymentLinkCreatorDetails[];
  campaignSlugName: string;
}

export interface PaymentLinkCreatorDetails {
  username?: string;
  socialNetwork?: SocialCreatorsPlatforms;
}

export interface CreatorsPaymentLink {
  link?: string;
  email?: string;
  username?: string;
}

export interface PaginationRequest {
  disable?: boolean;
  page?: number;
  pageSize?: number;
}

export enum RecipientStatus {
  Active = 'ACTIVE',
  Incomplete = 'INCOMPLETE',
}

export enum RecipientStatusFilter {
  ACTIVE = 'ACTIVE',
  INCOMPLETE = 'INCOMPLETE',
  NOT_REGISTERED = 'NOT_REGISTERED',
  UNDER_REVIEW = 'UNDER_REVIEW',
  ALL = 'ALL',
}

export interface RecipientSearchFilters {
  consumerRecipientIds?: string[];
  statuses?: RecipientStatus[];
}

export interface RecipientSearchRequest {
  filters?: RecipientSearchFilters;
  paginate?: PaginationRequest;
}

export interface PaymentRecipient {
  consumerRecipientId?: string;
  status?: RecipientStatus;
  consentedAt?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export type PaymentRecipientListResponse = PaginatedResponse<PaymentRecipient>;

export interface PaymentsRequestPaginate {
  disable: boolean;
  page?: number;
  pageSize?: number;
}

export interface CreatorsCampaignsPaymentsHistoryRequest {
  campaignSlugName: string;
  filters?: CreatorsCampaignsPaymentsHistoryRequestFilters;
  paginate?: PaymentsRequestPaginate;
}

export interface CreatorsCampaignsExportPaymentsHistoryRequest {
  campaignSlugName: string;
  filters?: CreatorsCampaignsPaymentsHistoryRequestFilters;
}

export interface CreatorsCampaignsPaymentsHistoryRequestFilters {
  searchTerm?: string;
  networks?: SocialCreatorsPlatforms[];
  endDate?: number;
  startDate?: number;
  payoutMethods?: PaymentsPayoutMethodsType[];
  statuses?: PaymentsTransactionStatusType[];
}

export interface CreatorsCampaignsPaymentsTransactionItem {
  amount?: string;
  createdAt?: Date;
  creator?: CampaignCreatorInfo;
  currency?: string;
  failureMessage?: string;
  payoutMethod?: PaymentsPayoutMethodsType;
  payerUser?: User;
  status?: PaymentsTransactionStatusType;
  transactionId?: string;
  updatedAt?: Date;
}

export interface User {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export type CreatorsCampaignsPaymentsHistoryResponse =
  PaginatedResponse<CreatorsCampaignsPaymentsTransactionItem>;

export interface PaymentsTransactionsAggregateStatsRequest {
  filters?: {
    campaignSlugNames?: string[];
    creatorIds?: string[];
    endDate?: string;
    payoutMethods?: PaymentsPayoutMethodsType[];
    startDate?: string;
    status?: PaymentsTransactionStatusType[];
    transactionType?: PaymentsTransactionType[];
  };
  targetCurrency?: string;
  paginate?: { disable?: boolean; page?: number; pageSize?: number };
}

export interface CampaignPaymentsTransactionsAggregateResponse {
  commission?: MonetaryValue;
  fixedPay?: MonetaryValue;
  paid?: MonetaryValue;
  pending?: MonetaryValue;
  sent?: MonetaryValue;
}

export interface PaymentsTransactionsAggregation {
  currency?: string;
  totalPaid?: number;
  totalProcessing?: number;
}

export interface PaymentsTransactionsAggregateResponse {
  debit?: PaymentsTransactionsAggregation;
  credit?: PaymentsTransactionsAggregation;
}

export type CreatorsCampaignsPaymentsListResponse = PaginatedResponse<{
  name?: string;
  username?: string;
  creatorId?: string;
  email?: string;
  network?: SocialCreatorsPlatforms;
  profilePictureUrl?: string;
  mediaCount?: number;
  recipientStatus?: RecipientStatus;
  creatorPortalProfile?: CreatorPortalProfile;
  payout?: CreatorCampaignPayoutBreakdown;
  metadata?: {
    trolleyComplianceStatus?: string;
    trolleyTaxStatus?: string;
  };
}>;

export interface CreatorsCampaignsPaymentsListRequest {
  affiliateType?: AffiliateType;
  allInfluencers?: boolean;
  campaignSlugName?: string;
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  skipMediaAggregate?: boolean;
  statuses?: RecipientStatusFilter[];
}
